import React from 'react';

// Custom hook for counter animation
const useCounter = (end: number, duration: number = 2000, shouldStart: boolean = false) => {
  const [count, setCount] = React.useState(0);
  
  React.useEffect(() => {
    if (!shouldStart) return;
    
    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, shouldStart]);
  
  return count;
};

const AgencyShowcase: React.FC = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const statsRef = React.useRef<HTMLDivElement>(null);
  
  const agentsCount = useCounter(1000, 2000, isVisible);
  const premiumCount = useCounter(40, 2000, isVisible);
  const carrierCount = useCounter(28, 2000, isVisible);
  
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3, rootMargin: '50px' }
    );
    
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-16 sm:py-20 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            The numbers so far.
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We've been doing this since 2022. Here's where we're at.
          </p>
        </div>
        
        {/* Stats */}
        <div ref={statsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
            <div className="text-4xl font-bold text-navy-700 mb-2">
              {agentsCount}+
            </div>
            <div className="text-slate-600">Agents we work with</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
            <div className="text-4xl font-bold text-navy-700 mb-2">
              ${premiumCount}M+
            </div>
            <div className="text-slate-600">Premium written annually</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
            <div className="text-4xl font-bold text-navy-700 mb-2">
              {carrierCount}+
            </div>
            <div className="text-slate-600">Carrier partnerships</div>
          </div>
        </div>

        {/* What makes us different */}
        <div className="bg-white border border-slate-200 rounded-xl p-8">
          <h3 className="text-xl font-semibold text-slate-900 mb-6 text-center">
            Why agents stick with us
          </h3>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-lg font-medium text-slate-900 mb-2">We actually answer</div>
              <p className="text-sm text-slate-600">
                Questions in Discord get responses. Problems get solved. You're not shouting into a void.
              </p>
            </div>
            <div>
              <div className="text-lg font-medium text-slate-900 mb-2">No bait and switch</div>
              <p className="text-sm text-slate-600">
                The comp we quote is the comp you get. The leads we sell are the leads you receive. Simple.
              </p>
            </div>
            <div>
              <div className="text-lg font-medium text-slate-900 mb-2">We're still growing</div>
              <p className="text-sm text-slate-600">
                Started in 2022, still building. If you want to be part of something that's going somewhere, this is it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AgencyShowcase;
